<template>
  <div class="scroll-container">
    <div class="scroll-content">
      <v-table fixed-header class="screen-element-table" style="max-height:400px">
        <thead>
          <tr>
            <th class="border-0 bg-grey-lighten-1"></th>

            <template v-for="group in groupedQuestions" :key="group.id">
              <th
                :colspan="group.questions.length"
                class="text-center text-uppercase font-weight-black bg-grey-lighten-1"
              >
                {{ group.label }}
              </th>
            </template>
          </tr>
          <tr>
            <th class="bg-grey-lighten-2 border-0"></th>

            <template v-for="group in groupedQuestions" :key="group.id">
              <th
                v-for="(question, questionKey) in group.questions"
                :key="questionKey"
                class="bg-grey-lighten-2"
              >
                <div class="text-body-1 font-weight-bold text-center question-cell-header">
                  <v-icon size="small" :icon="mdiStar" v-if="question.mandatory" />
                  {{ question.label }} {{ question.mandatory && '*' || '' }}
                </div>
              </th>
            </template>
          </tr>
        </thead>

        <tbody>
          <template
            v-for="readingElement of readingElements"
            :key="readingElement.id"
          >
            <tr v-if="!readingElement?.to_remove">
              <td class="bg-grey-lighten-3 cell-none border-0 pl-0 pr-1">
                <delete-screen-by-element
                  v-if="!presenceIsFalse"
                  :readingElement="readingElement"
                  :readingElementId="readingElement.id"
                />
              </td>

              <template v-for="group in groupedQuestions" :key="group.id">
                <questionnaire-condensed-form-constructor
                  :questions="group.questions"
                  :groupId="group.id"
                  :attachmentType="READING.ATTACHMENT_TYPE.ELEMENT"
                  :attachmentId="readingElement.id"
                  :questions-conditions="questionsConditionsByElement['by_element|'+readingElement.id] || {}"
                  :trigger-reload="triggerReload"
                  @answeredToAQuestion="answeredToAQuestion"
                  @updateQuestionsConditions="updateQuestionsConditions"
                />
              </template>
            </tr>
          </template>
        </tbody>
      </v-table>
    </div>
  </div>

  <div class="mx-3 my-5">
    <add-screen-by-element
      v-if="!presenceIsFalse"
      @updateQuestionsConditions="updateQuestionsConditions"
    />
  </div>
</template>

<script setup>
import { computed, inject, onMounted, provide, ref, watch } from 'vue'
import { mdiStar } from '@mdi/js';
import { useApplicationStore } from '@/stores/applicationStore';
import { useStoreManager } from '@/stores/storeManager';
import { extractReadingIdByParams,
  extractReadingAnswersByReadingId } from '@/stores/manager/readingManager';
import { READING, VALUE } from '@/constants';
import QuestionnaireCondensedFormConstructor from '@/components/mission-planning/questionnaires/parts/QuestionnaireCondensedFormConstructor.vue';
import AddScreenByElement from '@/components/mission-planning/questionnaires/modals/AddScreenByElement.vue';
import DeleteScreenByElement from '@/components/mission-planning/questionnaires/modals/DeleteScreenByElement.vue';
import { extractScreenQuestions, groupedQuestionsByGroupId } from '@/stores/manager/configurationManager'
import { useRoute } from 'vue-router'
import { createReadingConditionStore } from '@/stores/conditionStore'

const props = defineProps({
  screen: {
    type: Object,
    required: true
  },
  groupedQuestions: {
    type: Object,
    required: false
  },
  attachmentLoopItems: {
    type: Object,
    required: false
  },
});

const storeManager = useStoreManager();
const applicationStore = useApplicationStore();
const route = useRoute();

const currentMissionPlanningId = inject('currentMissionPlanningId');

const userDetails = computed(() => applicationStore.userDetails);
provide('userDetails', userDetails);

const showHiddenQuestions = computed(() => applicationStore.showHiddenQuestions);
provide('showHiddenQuestions', showHiddenQuestions);

const missionPlanning = computed(
  () =>
  storeManager.getFromStoreByCategory('mission', 'missionsPlannings')[currentMissionPlanningId.value] || {}
);
provide('missionPlanning', missionPlanning);

const mission = computed(
  () =>
  storeManager.getFromStoreByCategory('mission', 'missions')[missionPlanning.value?.mission?.id] || {}
);
provide('mission', mission);

const configuration = computed(
  () =>
  storeManager.getFromStoreByCategory('configuration', 'configurations')[mission.value?.configuration?.id] || {}
);
provide('configuration', configuration);

const readings = computed(() =>
  storeManager.getFromStoreByCategory('reading', 'reading') || {}
);
provide('readings', readings);

const readingAnswers = computed(() =>
  storeManager.getFromStoreByCategory('reading', 'answer') || {}
);
provide('readingAnswers', readingAnswers);

const readingsHistory = computed(() =>
  storeManager.getFromStoreByCategory('readingHistory', 'reading') || {}
);
provide('readingsHistory', readingsHistory);

const currentReadingId = computed(() =>
  extractReadingIdByParams(
    readings.value,
    mission.value?.configuration?.id,
    currentMissionPlanningId.value,
    userDetails.value?.id
  )
);
provide('currentReadingId', currentReadingId);

const originalReading = computed(() => readings.value[currentReadingId.value] || {});
provide('originalReading', originalReading);

const readingElements = computed(() =>
    Object.entries(storeManager.getFromStoreByCategory('reading', 'element'))?.map(([elId,el]) => {
      return {
        ...el,
        id: elId
      }
    })?.sort(
        (a, b) => a.creation_date - b.creation_date
    ).filter(el => el.reading === currentReadingId.value) || []
)
provide('readingElements', readingElements)
const currentScreenId = computed(() => route.params.screen_id);
provide('currentScreenId', currentScreenId);
const screens = computed(
  () =>
    configuration.value?.screen?.filter(
      (screen) => screen.id?.toString() === currentScreenId.value?.toString()
    ) || []);
const screenQuestions = computed(() =>
  extractScreenQuestions(Object.values(configuration.value?.questions || {}), screens.value)
);
const groupedQuestions = computed(() =>
  groupedQuestionsByGroupId(screenQuestions.value, currentReadingId.value || VALUE.DEFAULT)
);

const questionsIdsPerGroup = computed(() => {
  return groupedQuestions.value.reduce((acc, group) => {
    acc[group.id] = group.questions.map(q => q.id);
    return acc;
  }, {});
});

const foundAnswersPerGroup = computed(() => {
  let output = {};
  for (const [groupId, questionsIds] of Object.entries(questionsIdsPerGroup.value||{})) {
    output[groupId] = extractReadingAnswersByReadingId(
      readingAnswers.value,
      currentReadingId.value,
      questionsIds
    )
  }
  return output;
});
provide('foundAnswersPerGroup', foundAnswersPerGroup);
const questionsConditions = ref({});
const localAnswers = ref({});
const currentElementId = ref(null);
const triggerReload = ref(false);
const answers = computed(
  () =>
    Object.values(storeManager.getFromStoreByCategory('reading', 'answer'))
      .filter(answer => answer.reading?.toString() === currentReadingId.value?.toString())
    || {});
const answeredToAQuestion = ({attachmentId, answers}) => {
  currentElementId.value = attachmentId;
  localAnswers.value = answers;
};
function groupByFullSection(data) {
  const grouped = { default: {} };

  Object.entries(data).forEach(([key, value]) => {
    const match = key.match(/:(by_element\|([^|]+))$/); // Extract "by_element|xxx" if exists

    if (match) {
      const section = match[1]; // "by_element|xxx"
      if (!grouped[section]) {
        grouped[section] = {};
      }
      grouped[section][key] = value;
    } else {
      grouped.default[key] = value;
    }
  });

  return grouped;
}

const questionsConditionsByElement = computed(() => {
  return groupByFullSection(questionsConditions.value);
});
const updateQuestionsConditions = (qc) => {
  questionsConditions.value = qc;
  triggerReload.value = !triggerReload.value;
}
const presenceIsFalse = computed(() => {
  return !originalReading.value?.data?.isPresentDate && configuration.value?.settings?.promoter_geolocation
})

watch(() => answers.value, (a) => {
  localAnswers.value = a;
}, {immediate: true, deep: true});

onMounted(() => {
  watch(() => currentReadingId.value, () => {
    updateQuestionsConditions(createReadingConditionStore(currentReadingId.value || VALUE.DEFAULT)?.questions || {});
  }, {immediate: true});
})
</script>

<style scoped>
.screen-element-table {
  border-collapse: collapse;
}

.screen-element-table th,
td {
  border: 1px solid #d1d1d1;
  background-color: white;
}

.question-cell-header {
  width: 250px !important;
  white-space: initial;
}
</style>